import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Lock, FileDown, Search, ShieldCheck } from "lucide-react";
import { http, formatApiError } from "../lib/api";
import { SiteForms } from "../components/agent/SiteForms";
import { AgentInstructions } from "../components/agent/AgentInstructions";
import { AgentVisitors } from "../components/agent/AgentVisitors";
import AgentRounds from "../components/agent/AgentRounds";
import { PtiPanel, PtiOverlay } from "../components/agent/AgentPti";
import { usePtiController } from "../components/agent/usePtiController";
import AgentMessages from "./AgentMessages";

const ipt = "border border-slate-300 rounded-md px-2 py-1.5 text-sm bg-white";
const fmt = (iso) => new Date(iso).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short", timeZone: "Europe/Paris" });
const devKey = (pid) => localStorage.getItem(`pcs_dev_${pid}`) || "";

const ExportDialog = ({ payload, onClose }) => {
  const [v, setV] = useState({ reason: "", recipient: "", requester: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const go = async (e) => {
    e.preventDefault();
    const miss = [["reason", "le motif"], ["recipient", "le destinataire"], ["requester", "le demandeur"]].filter(([k]) => v[k].trim().length < 2).map(([, l]) => l);
    if (miss.length) { setErr(`Indiquez ${miss.join(", ")}.`); return; }
    setBusy(true); setErr("");
    try {
      const r = await http.post("/pcs/export", { ...payload, ...v }, { responseType: "blob" });
      const href = URL.createObjectURL(r.data);
      Object.assign(document.createElement("a"), { href, download: "extraction-pcs.pdf" }).click();
      setTimeout(() => URL.revokeObjectURL(href), 2000);
      toast.success("Extraction PDF enregistrée dans la main courante"); onClose();
    } catch (er) { setErr(formatApiError(er)); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 grid place-items-center p-4" data-testid="pcs-export-dialog">
      <form onSubmit={go} noValidate className="bg-white rounded-lg w-full max-w-md p-5 space-y-3">
        <h2 className="font-display font-bold text-lg">Extraction PDF</h2>
        <p className="text-xs text-slate-500">Obligatoire : l'extraction est tracée (agent, date, motif, destinataire, demandeur).</p>
        {[["reason", "Motif"], ["recipient", "Destinataire"], ["requester", "Demandeur"]].map(([k, l]) => (
          <label key={k} className="block text-xs font-semibold text-slate-600">{l}<input required minLength={k === "reason" ? 3 : 2} value={v[k]} onChange={(e) => setV({ ...v, [k]: e.target.value })} className={`${ipt} w-full mt-1`} data-testid={`pcs-export-${k}`} /></label>
        ))}
        {err && <p className="text-sm text-rose-700" data-testid="pcs-export-error">{err}</p>}
        <div className="flex justify-end gap-2"><button type="button" onClick={onClose} className="px-3 py-1.5 text-sm border border-slate-300 rounded-md">Annuler</button>
          <button disabled={busy} className="px-3 py-1.5 text-sm font-semibold rounded-md bg-[#0B2A7A] text-white disabled:opacity-50" data-testid="pcs-export-submit">{busy ? "Extraction…" : "Extraire le PDF"}</button></div>
      </form>
    </div>
  );
};

const Filters = ({ f, setF, children }) => (
  <div className="flex flex-wrap items-end gap-2 mb-3">
    <label className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Du<input type="date" value={f.start} onChange={(e) => setF({ ...f, start: e.target.value })} className={`${ipt} block`} data-testid="pcs-f-start" /></label>
    <label className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Au<input type="date" value={f.end} onChange={(e) => setF({ ...f, end: e.target.value })} className={`${ipt} block`} data-testid="pcs-f-end" /></label>
    {children}
    <div className="relative flex-1 min-w-[200px]"><Search className="h-4 w-4 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
      <input value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} placeholder="Rechercher (code 0101, mot, agent…)" className={`${ipt} w-full pl-8`} data-testid="pcs-f-q" /></div>
  </div>
);

const useList = (url, f, tick) => {
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState("");
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    let live = true;
    setErr("");
    const t = setTimeout(() => http.get(url, { params: Object.fromEntries(Object.entries(f).filter(([, v]) => v)) }).then((r) => live && setRows(r.data)).catch((e) => { if (live) { setRows([]); setErr(formatApiError(e)); } }), 250);
    return () => { live = false; clearTimeout(t); };
  }, [url, f, tick, retry]);
  const errEl = err && <p className="text-sm text-rose-700 mb-2" data-testid="pcs-list-error">{err} <button type="button" onClick={() => setRetry(retry + 1)} className="underline font-semibold" data-testid="pcs-list-retry">Réessayer</button></p>;
  return [rows, errEl];
};

const NoteBox = ({ siteId, onSaved }) => {
  const [text, setText] = useState("");
  const [urgent, setUrgent] = useState(false);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { await http.post("/agent-app/logbook", { site_id: siteId, text: text.trim(), urgent }); setText(""); setUrgent(false); toast.success(urgent ? "Incident urgent inscrit : responsables alertés" : "Note ajoutée à la main courante"); onSaved(); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-3 mb-3 flex gap-2" data-testid="pcs-note">
      <textarea rows={2} value={text} onChange={(e) => setText(e.target.value)} maxLength={2000} placeholder="Nouvelle inscription en main courante (événement, passage, appel…)" className={`${ipt} flex-1`} data-testid="pcs-note-text" />
      <label className="flex flex-col items-center justify-center gap-1 px-2 text-[11px] font-bold text-rose-700 cursor-pointer" data-testid="pcs-note-urgent-label"><input type="checkbox" checked={urgent} onChange={(e) => setUrgent(e.target.checked)} data-testid="pcs-note-urgent" />Urgent</label>
      <button type="button" disabled={busy || !text.trim()} onClick={save} className="px-4 text-sm font-semibold rounded-md bg-[#0B2A7A] text-white disabled:opacity-50" data-testid="pcs-note-save">{busy ? "…" : "Inscrire"}</button>
    </div>
  );
};

const History = ({ ctx }) => {
  const [f, setF] = useState({ start: "", end: "", q: "", kind: "" });
  const [tick, setTick] = useState(0);
  const [rows, errEl] = useList("/pcs/history", f, tick);
  const [exp, setExp] = useState(null);
  return (
    <div data-testid="pcs-history">
      <NoteBox siteId={ctx.site?.id} onSaved={() => setTick(tick + 1)} />
      <Filters f={f} setF={setF}>
        <select value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value })} className={ipt} data-testid="pcs-f-kind"><option value="">Toutes les activités</option>{Object.entries(ctx.kinds).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
        <button type="button" onClick={() => setExp({ what: "history", ...f })} className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-semibold rounded-md bg-slate-900 text-white" data-testid="pcs-history-export"><FileDown className="h-4 w-4" />PDF</button>
      </Filters>
      {errEl}
      {!rows ? <p className="text-sm text-slate-500">Chargement…</p> : !rows.length ? <p className="text-sm text-slate-500" data-testid="pcs-history-empty">Aucune activité.</p> : (
        <ul className="bg-white border border-slate-200 rounded-lg divide-y divide-slate-100">
          {rows.map((r) => <li key={r.id} className="px-3 py-2 text-sm flex gap-3" data-testid={`pcs-log-${r.id}`}><span className="font-mono text-xs text-slate-500 w-28 shrink-0">{fmt(r.at)}</span>
            <span className="text-[10px] font-bold uppercase tracking-widest text-[#0B2A7A] w-28 shrink-0">{ctx.kinds[r.kind] || r.kind}</span><span className="font-semibold w-40 shrink-0 truncate">{r.agent_name}</span><span className="text-slate-700">{r.text}</span></li>)}
        </ul>)}
      {exp && <ExportDialog payload={exp} onClose={() => setExp(null)} />}
    </div>
  );
};

const Reports = ({ ctx }) => {
  const [f, setF] = useState({ start: "", end: "", q: "", category_id: "" });
  const [tick, setTick] = useState(0);
  const [rows, errEl] = useList("/pcs/reports", f, tick);
  const [exp, setExp] = useState(null);
  const color = (id) => ctx.categories.find((c) => c.id === id)?.color;
  return (
    <div className="space-y-5" data-testid="pcs-reports">
      <SiteForms onSent={() => setTick(tick + 1)} />
      <section>
        <h2 className="font-display text-lg font-extrabold mb-2">Rapports du site (tous agents)</h2>
        <Filters f={f} setF={setF}>
          <select value={f.category_id} onChange={(e) => setF({ ...f, category_id: e.target.value })} className={ipt} data-testid="pcs-f-category"><option value="">Toutes catégories</option>{ctx.categories.map((c) => <option key={c.id} value={c.id}>{c.number} · {c.name}</option>)}</select>
        </Filters>
        {errEl}
        {!rows ? <p className="text-sm text-slate-500">Chargement…</p> : !rows.length ? <p className="text-sm text-slate-500" data-testid="pcs-reports-empty">Aucun rapport.</p> : (
          <ul className="bg-white border border-slate-200 rounded-lg divide-y divide-slate-100">
            {rows.map((r) => <li key={r.id} className="px-3 py-2 text-sm flex items-center gap-3" data-testid={`pcs-report-${r.id}`} style={r.category_id ? { borderLeft: `5px solid ${color(r.category_id)}` } : undefined}>
              <span className="font-mono text-xs text-slate-500 w-28 shrink-0">{fmt(r.created_at)}</span>
              {r.code && <span className="font-mono text-[11px] font-extrabold rounded px-1.5 py-0.5 text-white" style={{ background: color(r.category_id) || "#64748B" }}>{r.code}</span>}
              <span className="font-semibold">{r.title}</span><span className="text-slate-500 truncate">{r.agent_name}</span>
              <button type="button" onClick={() => setExp({ what: "report", report_id: r.id })} className="ml-auto p-1.5 rounded hover:bg-slate-100" title="Extraire en PDF" data-testid={`pcs-report-export-${r.id}`}><FileDown className="h-4 w-4" /></button></li>)}
          </ul>)}
      </section>
      {exp && <ExportDialog payload={exp} onClose={() => setExp(null)} />}
    </div>
  );
};

const TABS = [["history", "Main courante"], ["reports", "Rapports"], ["consignes", "Consignes"], ["visitors", "Visiteurs"], ["rounds", "Rondes"], ["pti", "PTI"], ["messages", "Messagerie"]];

const PcsApp = ({ ctx, onLock }) => {
  const [tab, setTab] = useState("history");
  const pti = usePtiController();
  return (
    <div className="min-h-screen bg-slate-100" data-testid="pcs-app">
      <PtiOverlay pti={pti} />
      <header className="bg-[#061A55] text-white px-4 py-3 flex flex-wrap items-center gap-3">
        <ShieldCheck className="h-6 w-6 text-[#E8C46A]" />
        <div><div className="font-display font-extrabold" data-testid="pcs-title">{ctx.post?.name} — {ctx.site?.name}</div><div className="text-xs text-[#C9D6F5]">{ctx.agent_name} · {ctx.device} · verrouillage après {ctx.idle_minutes} min d'inactivité</div></div>
        <button type="button" onClick={onLock} className="ml-auto inline-flex items-center gap-1.5 bg-white/10 hover:bg-white/20 rounded-md px-3 py-2 text-sm font-semibold" data-testid="pcs-lock"><Lock className="h-4 w-4" />Verrouiller</button>
      </header>
      <nav className="flex gap-1 overflow-x-auto bg-white border-b border-slate-200 px-2" data-testid="pcs-tabs">
        {TABS.map(([k, l]) => <button key={k} type="button" onClick={() => setTab(k)} className={`px-4 py-3 text-sm font-bold border-b-2 whitespace-nowrap ${tab === k ? "border-[#0B2A7A] text-[#0B2A7A]" : "border-transparent text-slate-500"}`} data-testid={`pcs-tab-${k}`}>{l}</button>)}
      </nav>
      <main className="p-4 max-w-[1200px] mx-auto">
        {tab === "history" ? <History ctx={ctx} /> : tab === "reports" ? <Reports ctx={ctx} /> : tab === "consignes" ? <AgentInstructions /> : tab === "visitors" ? <AgentVisitors />
          : tab === "rounds" ? <AgentRounds /> : tab === "pti" ? <PtiPanel pti={pti} /> : <AgentMessages />}
      </main>
    </div>
  );
};

const Gate = ({ pid, info, onIn, onEnrolled }) => {
  const [v, setV] = useState({ email: "", pin: "", code: "", label: "Téléphone PTI" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const run = async (fn) => { setBusy(true); setErr(""); try { await fn(); } catch (e) { setErr(formatApiError(e)); } finally { setBusy(false); } };
  const login = (e) => { e.preventDefault(); run(async () => { await http.post(`/pcs-public/${pid}/login`, { email: v.email, pin: v.pin, device_key: devKey(pid) }); onIn(); }); };
  const enroll = (e) => { e.preventDefault(); run(async () => { const r = await http.post(`/pcs-public/${pid}/enroll`, { code: v.code, label: v.label }); localStorage.setItem(`pcs_dev_${pid}`, r.data.device_key); onEnrolled(); }); };
  const input = (k, props) => <input {...props} value={v[k]} onChange={(e) => setV({ ...v, [k]: e.target.value })} className="mt-1 w-full bg-white/10 border border-white/20 rounded-lg px-3 py-3 text-white" data-testid={`pcs-${k}`} />;
  return (
    <div className="min-h-screen grid place-items-center bg-[linear-gradient(180deg,#0B2A7A,#061A55)] text-white p-6" data-testid="pcs-gate">
      <div className="w-full max-w-sm">
        <ShieldCheck className="h-12 w-12 text-[#E8C46A] mx-auto mb-3" />
        <h1 className="font-display text-2xl font-extrabold text-center" data-testid="pcs-gate-title">{info.name}</h1>
        <p className="text-center text-sm text-[#C9D6F5] mb-6">{info.site_name} · {info.company_name}</p>
        {info.allowed ? (
          <form onSubmit={login} className="space-y-3" data-testid="pcs-login-form">
            <label className="block text-[10px] font-bold uppercase tracking-widest text-[#E8C46A]">E-mail{input("email", { type: "email", required: true, autoComplete: "off" })}</label>
            <label className="block text-[10px] font-bold uppercase tracking-widest text-[#E8C46A]">Code PIN{input("pin", { type: "password", inputMode: "numeric", required: true, autoComplete: "off" })}</label>
            {err && <p className="text-sm text-rose-300" data-testid="pcs-error">{err}</p>}
            <button disabled={busy} className="w-full bg-[#E8C46A] text-[#0B1A4A] font-bold rounded-lg py-3" data-testid="pcs-login-submit">{busy ? "Connexion…" : "Ouvrir le poste"}</button>
            <p className="text-xs text-[#C9D6F5] text-center">Réservé aux agents en service sur ce site.</p>
          </form>
        ) : (
          <form onSubmit={enroll} className="space-y-3" data-testid="pcs-enroll-form">
            <p className="text-sm text-amber-200" data-testid="pcs-not-allowed">Cet appareil n'est pas autorisé. S'il s'agit du téléphone PTI du site, saisissez le code d'enregistrement donné par le responsable.</p>
            <label className="block text-[10px] font-bold uppercase tracking-widest text-[#E8C46A]">Code d'enregistrement{input("code", { required: true, autoCapitalize: "characters" })}</label>
            <label className="block text-[10px] font-bold uppercase tracking-widest text-[#E8C46A]">Nom de l'appareil{input("label", {})}</label>
            {err && <p className="text-sm text-rose-300" data-testid="pcs-error">{err}</p>}
            <button disabled={busy} className="w-full bg-[#E8C46A] text-[#0B1A4A] font-bold rounded-lg py-3" data-testid="pcs-enroll-submit">Enregistrer l'appareil</button>
          </form>
        )}
      </div>
    </div>
  );
};

export default function Pcs() {
  const { postId } = useParams();
  const [info, setInfo] = useState(null);
  const [ctx, setCtx] = useState(null);
  const [err, setErr] = useState("");
  const timer = useRef(null);
  const loadInfo = useCallback(() => http.get(`/pcs-public/${postId}`, { params: { device_key: devKey(postId) } }).then((r) => setInfo(r.data)).catch((e) => setErr(formatApiError(e))), [postId]);
  const enter = useCallback(() => http.get("/pcs/context").then((r) => setCtx(r.data)).catch(() => setCtx(null)), []);
  const lock = useCallback(async () => { try { await http.post("/pcs/logout"); } catch { /* déjà verrouillé */ } setCtx(null); loadInfo(); }, [loadInfo]);
  useEffect(() => { loadInfo(); enter(); }, [loadInfo, enter]);
  useEffect(() => {
    const end = (e) => { if (ctx) toast.error(e.detail || "Poste verrouillé"); setCtx(null); };
    window.addEventListener("agent-session-ended", end);
    return () => window.removeEventListener("agent-session-ended", end);
  }, [ctx]);
  useEffect(() => {
    if (!ctx) return undefined;
    const reset = () => { clearTimeout(timer.current); timer.current = setTimeout(() => { toast.info("Poste verrouillé après inactivité"); lock(); }, ctx.idle_minutes * 60000); };
    const evs = ["mousemove", "keydown", "touchstart", "click", "scroll"];
    evs.forEach((ev) => window.addEventListener(ev, reset, { passive: true }));
    reset();
    return () => { clearTimeout(timer.current); evs.forEach((ev) => window.removeEventListener(ev, reset)); };
  }, [ctx, lock]);
  if (err) return <div className="min-h-screen grid place-items-center text-slate-600" data-testid="pcs-unknown">{err}</div>;
  if (!info) return <div className="min-h-screen grid place-items-center bg-[#061A55] text-[#E8C46A]">Chargement…</div>;
  return ctx ? <PcsApp ctx={ctx} onLock={lock} /> : <Gate pid={postId} info={info} onIn={enter} onEnrolled={loadInfo} />;
}
